import { useId, useState } from "react";
import type { FormEvent } from "react";
import { problemFieldErrors, problemMessage } from "@/api/client";
import type { CreateVendorRequest, Guid, UpdateVendorRequest, Vendor } from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./VendorDialog.css";

/**
 * ef-042-6 — the "New vendor" / rename dialog `BooksVendors.tsx` opens from its own
 * header action or a row's Edit button. Same shape as `ShiftDialog.tsx`: a real
 * `role="dialog"` overlay, not `RegisterEntryDialog.tsx`'s always-on-screen inline form
 * — `books-vendors.spec.ts`'s own `open()`/row helpers click a trigger BEFORE the name
 * field exists, so the composer cannot already be on screen the way the register one is.
 *
 * ONE FIELD. `CreateVendorRequest`/`UpdateVendorRequest` (api/types.ts, mirroring
 * `VendorEndpoints.cs`) carry nothing but `name` — no second field to seed or clear
 * between create and rename.
 */
export function VendorDialog({
  vendor,
  onClose,
  onCreate,
  onUpdate,
}: {
  /** `null` opens the dialog in create mode; a `Vendor` opens it pre-seeded for rename. */
  vendor: Vendor | null;
  /** Called on Cancel, Escape, an overlay click, AND after a successful save — same
   *  division `ShiftDialog.tsx` draws between "close the dialog" and "where focus goes
   *  next", which stays the caller's job (it holds the trigger). */
  onClose: () => void;
  onCreate: (body: CreateVendorRequest) => Promise<Vendor>;
  onUpdate: (id: Guid, body: UpdateVendorRequest) => Promise<Vendor>;
}) {
  const [name, setName] = useState(vendor?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const nameId = useId();
  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    const trimmed = name.trim();
    /*
     * @ui-check: client-side ONLY — `VendorEndpoints.ValidateName`'s own 1-80 character
     * check still runs after this and remains the actual contract enforcement. This
     * guard exists so an empty submit never reaches the network at all.
     */
    if (trimmed === "") {
      setFieldErrors({ name: ["A name is required."] });
      setFormError("A name is required.");
      return;
    }

    setSaving(true);
    try {
      if (vendor) {
        await onUpdate(vendor.id, { name: trimmed });
      } else {
        await onCreate({ name: trimmed });
      }
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      // A 409 name conflict ("A vendor with that name already exists.") lands here —
      // no fieldErrors accompany it, so the paragraph below renders it.
      setFormError(problemMessage(err, "Could not save this vendor."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title={<>{vendor ? `Edit vendor — ${vendor.name}` : "New vendor"}</>}
      size="sm"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {/* "Save vendor" satisfies BOTH books-vendors.spec.ts save-button locators
                at once — create's `/^(save|create|add)( vendor)?$/i` and rename's
                `/^(save|update)( vendor)?$/i` both match it — so one label serves both
                modes, rather than ShiftDialog's own two-different-labels split. */}
            {saving ? "Saving…" : "Save vendor"}
          </button>
        </>
      }
    >
      <form id={formId} className="vendor-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="vendor-dialog__field">
          <label htmlFor={nameId}>Name</label>
          <input
            id={nameId}
            type="text"
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={fieldErrors?.name ? true : undefined}
          />
          {fieldErrors?.name && (
            <p className="vendor-dialog__field-error" role="alert">
              {fieldErrors.name[0]}
            </p>
          )}
        </div>

        {formError && !fieldErrors?.name && (
          <p className="vendor-dialog__form-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
